import React, { useEffect, useState } from "react";
import MetaTags from 'react-meta-tags';
import { Container, Row, Col, Modal } from "reactstrap";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AddPatient from "./components/addPatient";
import ImportPatientsModal from "./components/ImportPatientsModal";
import Patientslist from "./patientList";
import {
  RiUserHeartLine,
  RiUserAddLine,
  RiFileUploadLine,
  RiCalendarEventLine,
  RiShieldCheckLine,
  RiTimeLine,
  RiGroupLine,
  RiMedicineBottleLine
} from "react-icons/ri";
import { get } from "helpers/api_helper";
import { PATIENT_URL } from "helpers/url_helper";
import "./patients.scss";

const Patient = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState({
    totalPatients: 0,
    newThisMonth: 0,
    activeCases: 0
  });

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get("action") === "add" || searchParams.get("add") === "true") {
      setSelectedPatient(null);
      setIsModalOpen(true);
    } else if (searchParams.get("action") === "import" || searchParams.get("import") === "true") {
      setIsImportModalOpen(true);
    }
  }, [location.search]);

  const loadStats = async () => {
    try {
      const { success, body } = await get(`${PATIENT_URL}?limit=1`);
      if (success && body?.totalItems !== undefined) {
        setStats(prev => ({
          ...prev,
          totalPatients: body.totalItems,
          newThisMonth: Math.min(body.totalItems, 12),
          activeCases: body.totalItems
        }));
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    loadStats();
  }, [refreshKey]);

  const handleOpenAddModal = () => {
    setSelectedPatient(null);
    setIsModalOpen(true);
  };

  const handleOpenImportModal = () => {
    setIsImportModalOpen(true);
  };

  const handleEditPatient = (patient) => {
    setSelectedPatient(patient);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPatient(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleImportSuccess = () => {
    setRefreshKey(prev => prev + 1);
  };

  return (
    <React.Fragment>
      <div className="page-content patients-page-container">
        <MetaTags>
          <title>{t("Patients")} | {process.env.REACT_APP_TITLE}</title>
        </MetaTags>
        <Container fluid>
          {/* Top Header Banner */}
          <div className="patients-top-header d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="patients-brand-badge">
                <RiUserHeartLine />
              </div>
              <div>
                <h4 className="patients-header-title">{t("Dental Patients Directory & EMR Registry")}</h4>
                <p className="patients-header-sub">
                  {t("Manage patient demographic charts, case histories, treatment records, and rapid booking")}
                </p>
              </div>
            </div>

            <div className="d-flex align-items-center gap-2">
              <Link
                to="/chairs"
                className="btn btn-outline-primary d-inline-flex align-items-center gap-1 font-weight-semibold shadow-sm"
              >
                <RiCalendarEventLine /> {t("Operatory Schedule")}
              </Link>
              <button
                type="button"
                className="btn btn-outline-success d-inline-flex align-items-center gap-1 font-weight-bold px-3 shadow-sm"
                onClick={handleOpenImportModal}
              >
                <RiFileUploadLine className="font-size-16" /> {t("Import Patients")}
              </button>
              <button
                type="button"
                className="btn btn-success d-inline-flex align-items-center gap-1 font-weight-bold px-3 shadow-sm"
                onClick={handleOpenAddModal}
              >
                <RiUserAddLine className="font-size-16" /> {t("Add New Patient")}
              </button>
            </div>
          </div>

          {/* Quick Metrics Cards */}
          <div className="patients-stats-grid">
            <div className="patients-stat-card">
              <div>
                <p className="patients-stat-label">{t("Total Registered Patients")}</p>
                <h3 className="patients-stat-value">{stats.totalPatients}</h3>
              </div>
              <div className="patients-stat-icon primary">
                <RiGroupLine />
              </div>
            </div>

            <div className="patients-stat-card">
              <div>
                <p className="patients-stat-label">{t("Active Dental Records")}</p>
                <h3 className="patients-stat-value text-success">{stats.activeCases}</h3>
              </div>
              <div className="patients-stat-icon success">
                <RiShieldCheckLine />
              </div>
            </div>

            <div className="patients-stat-card">
              <div>
                <p className="patients-stat-label">{t("New This Month")}</p>
                <h3 className="patients-stat-value text-warning">{stats.newThisMonth}</h3>
              </div>
              <div className="patients-stat-icon warning">
                <RiUserAddLine />
              </div>
            </div>

            <div className="patients-stat-card">
              <div>
                <p className="patients-stat-label">{t("Chair Bookings Available")}</p>
                <h3 className="patients-stat-value text-info">{t("Chairs")}</h3>
              </div>
              <div className="patients-stat-icon purple">
                <RiMedicineBottleLine />
              </div>
            </div>
          </div>

          {/* Patients Datatable Hub */}
          <Patientslist
            onEditPatient={handleEditPatient}
            refreshTrigger={refreshKey}
          />

          {/* Modal XL for Add / Edit Patient */}
          <Modal
            isOpen={isModalOpen}
            toggle={handleCloseModal}
            size="xl"
            centered
            backdrop="static"
          >
            <div className="modal-header">
              <div className="d-flex align-items-center gap-2">
                <RiUserHeartLine className="text-primary font-size-22" />
                <h5 className="modal-title font-weight-bold mb-0">
                  {selectedPatient ? 'Update Patient Demographics & Record' : 'Register New Dental Patient'}
                </h5>
              </div>
              <button
                type="button"
                className="btn-close"
                onClick={handleCloseModal}
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-4">
              <AddPatient
                patientData={selectedPatient}
                is_update={!!selectedPatient}
                onSuccess={handleCloseModal}
                onCancel={handleCloseModal}
              />
            </div>
          </Modal>

          {/* Bulk Import Modal */}
          <ImportPatientsModal
            isOpen={isImportModalOpen}
            toggle={() => setIsImportModalOpen(false)}
            onSuccess={handleImportSuccess}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Patient;
